import { defineStore } from "pinia";

/** Vorbelegung für „Neue Nachricht“ (siehe NewMessageDialog.open). */
export type ComposeWindowOptions = Record<string, unknown>;

/** Ergebnis eines Versands (Payload des `sent`-Ereignisses des Dialogs). */
export interface ComposeSentPayload {
  conversation?: { id?: number } | null;
  idCase?: number | null;
}

/**
 * App-globaler Zustand des Verfassen-Fensters „Neue Nachricht“
 * (Kundenfeedback 16.10.2026, Kanzlei Polz): „Es wäre gut, wenn man Mails
 * schreiben kann und zudem trotzdem im Programm weiter schauen kann, z. B.
 * ins Cockpit."
 *
 * Bisher gehörte der Dialog dem jeweils gemounteten MessageCenter. Ein
 * Seitenwechsel (Korrespondenz → Cockpit) zerstörte ihn samt Eingabe. Jetzt
 * lebt das Fenster – wie das Unterhaltungs-Fenster – in App.vue und überlebt
 * jede Navigation; die Ansichten reden nur noch über diesen Store mit ihm.
 */
export const useComposeWindowStore = defineStore("composeWindow", {
  state: (): {
    /** Zähler: jede Erhöhung öffnet das Fenster mit `options`. */
    openTick: number;
    options: ComposeWindowOptions;
    /** Fenster ist offen (auch minimiert). */
    isOpen: boolean;
    /** Zähler: wird nach jedem erfolgreichen Versand erhöht. */
    sentTick: number;
    lastSent: ComposeSentPayload | null;
    /** Zähler: wird bei jedem Schließen erhöht (Entwürfe neu einlesen). */
    closedTick: number;
  } => ({
    openTick: 0,
    options: {},
    isOpen: false,
    sentTick: 0,
    lastSent: null,
    closedTick: 0,
  }),

  actions: {
    /** Öffnet das Fenster (bzw. holt es nach vorn) mit der Vorbelegung. */
    open(options: ComposeWindowOptions = {}) {
      this.options = { ...options };
      this.isOpen = true;
      this.openTick += 1;
    },

    /** Vom Dialog gemeldet: Nachricht versendet. */
    notifySent(payload: ComposeSentPayload | null = null) {
      this.lastSent = payload ? { ...payload } : null;
      this.sentTick += 1;
    },

    /** Vom Dialog gemeldet: Fenster geschlossen (Entwurf bleibt erhalten). */
    notifyClosed() {
      this.isOpen = false;
      // Vorbelegung (ggf. mit Datei-Anhängen) nicht länger im Speicher halten.
      this.options = {};
      this.closedTick += 1;
    },
  },
});
